import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, CheckCircle2, Star, Phone, Mail, MapPin, Clock,
  Shield, Users, Award, Heart, BadgeCheck, Stethoscope, Activity,
  Bone, Footprints, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';
import SEO from '../components/SEO';
import MobileCarousel from '../components/MobileCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/api/services`);
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        } else {
          // Fallback to mock data if API fails
          setServices(mockData.services);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback to mock data
        setServices(mockData.services);
      } finally {
        setServicesLoading(false);
      }
    };

    fetchServices();
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: "Expert Care",
      description: "MSc qualified physiotherapist with 7+ years experience"
    },
    {
      icon: Users,
      title: "Patient-Centered",
      description: "Personalized treatment plans tailored to your goals"
    },
    {
      icon: Award,
      title: "Proven Results",
      description: "5/5 patient satisfaction rating"
    },
    {
      icon: Heart,
      title: "Comprehensive Service",
      description: "From assessment to full recovery and beyond"
    }
  ];

  const conditions = [
    { icon: Activity, title: "Back Pain", slug: "back-pain", description: "Lower back pain, upper back pain, disc problems" },
    { icon: Bone, title: "Knee Pain", slug: "knee-pain", description: "Arthritis, ligament injuries, runner's knee" },
    { icon: Activity, title: "Shoulder Pain", slug: "shoulder-pain", description: "Frozen shoulder, rotator cuff injuries" },
    { icon: Stethoscope, title: "Neck Pain", slug: "neck-pain", description: "Whiplash, stiffness, headaches" },
    { icon: Zap, title: "ACL Injuries", slug: "acl-injuries", description: "Pre & post-operative rehabilitation" },
    { icon: Activity, title: "Sports Injuries", slug: "sports-injuries", description: "Running injuries, tennis elbow, strains" },
    { icon: Footprints, title: "Sciatica", slug: "sciatica", description: "Nerve pain, numbness, tingling" },
    { icon: Stethoscope, title: "Post-Op Recovery", slug: "post-operative-recovery", description: "Joint replacement, surgery rehab" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Physiotherapist in Harrow | Same-Day Appointments | MSK and Sports Physio"
        description="Expert physiotherapy care for sports injuries, post-operative recovery, and pain management in Harrow. Same-day appointments available. MSc qualified with 7+ years experience at HarroWall - Call 07747075809"
        keywords="physiotherapist in Harrow, physio in Harrow, physiotherapy Harrow, sports injury physio Harrow, post-operative recovery Harrow, pain management physiotherapy Harrow, MSK physiotherapist Harrow, best physiotherapist in Harrow, sports physio Harrow, physiotherapist near me Harrow, same day physio Harrow"
        type="website"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              Regain Strength, Restore Movement
            </h1>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <span className="text-gray-700 font-medium text-sm">5.0 Rating | 50+ Google Reviews</span>
            </div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-8 font-medium leading-relaxed">
              Expert physiotherapy care for sports injuries, post-operative recovery, and pain management in Harrow
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg"
              >
                Book Your Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:07747075809'}
                size="lg"
                variant="outline"
                className="px-8 py-6 text-lg rounded-lg border-2 border-gray-300 hover:border-blue-600"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call: 07747075809
              </Button>
            </div>
          </div>

          {/* Hero Image */}
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/images/hero.png" 
                alt="Injury Prevention, Rehabilitation and Strength training - Physiotherapy in Harrow"
                width="1200"
                height="675"
                loading="eager"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Professional Credentials Badge */}
      <section className="py-8 px-4 lg:px-8 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:flex md:flex-wrap items-start md:items-center justify-center gap-x-6 gap-y-4 md:gap-6 lg:gap-12">
            <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
              <BadgeCheck className="w-8 h-8 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
              <div className="text-center md:text-left">
                <p className="text-xs md:text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap">HCPC Registered</p>
                <p className="text-xs text-gray-600 hidden sm:block">Health & Care Professions Council</p>
              </div>
            </div>
            <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
              <Shield className="w-8 h-8 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
              <div className="text-center md:text-left">
                <p className="text-xs md:text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap">CSP Member</p>
                <p className="text-xs text-gray-600 hidden sm:block">Chartered Society of Physiotherapy</p>
              </div>
            </div>
            <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
              <Award className="w-8 h-8 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
              <div className="text-center md:text-left">
                <p className="text-xs md:text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap">MSc Qualified</p>
                <p className="text-xs text-gray-600 hidden sm:block">Sports Medicine Specialist</p>
              </div>
            </div>
            <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
              <Users className="w-8 h-8 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
              <div className="text-center md:text-left">
                <p className="text-xs md:text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap">7+ Years Experience</p>
                <p className="text-xs text-gray-600 hidden sm:block">NHS & Private Practice</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Specialist Services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive physiotherapy care tailored to your needs
            </p>
          </div>

          {/* Desktop Grid (tablet & up) */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesLoading ? (
              // Loading skeleton
              Array.from({ length: 6 }).map((_, idx) => (
                <Card key={idx} className="border-2 border-gray-200 animate-pulse">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded mb-3 w-3/4 mx-auto"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6 mx-auto"></div>
                  </CardContent>
                </Card>
              ))
            ) : (
              services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group block"
                  data-testid={`service-card-${service.slug}`}
                >
                  <Card className="border-2 border-gray-200 group-hover:border-blue-500 group-hover:shadow-lg transition-all duration-300 h-full">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-blue-100 group-hover:bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                        <div className="w-8 h-8 bg-blue-600 group-hover:bg-white rounded-full transition-colors"></div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-3">{service.name}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.description}</p>
                      <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">
                        Learn more
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))
            )}
          </div>

          {/* Mobile Carousel (below md) */}
          <div className="md:hidden">
            <MobileCarousel testId="services-carousel">
              {(servicesLoading ? [] : services).map((service) => (
                <Link key={service.id} to={`/services/${service.slug}`} className="block h-full">
                  <Card className="border-2 border-gray-200 h-full">
                    <CardContent className="p-6 text-center">
                      <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <div className="w-7 h-7 bg-blue-600 rounded-full"></div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{service.name}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </MobileCarousel>
          </div>
        </div>
      </section>

      {/* Meet Param Section */}
      <section id="team" className="py-20 px-4 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Meet the Founder: <span className="text-blue-600">Param Jumani</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lead Physiotherapist</h3>
              
              {/* Professional Credentials */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  HCPC Registered
                </span>
                <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  <Shield className="w-3.5 h-3.5" />
                  CSP Member
                </span>
                <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  <Award className="w-3.5 h-3.5" />
                  7+ Years Experience
                </span>
              </div>
              
              <p className="text-lg text-gray-600 mb-6">
                <strong>Qualifications:</strong> BSc Physiotherapy, MSc Sports Medicine - University of Central Lancashire
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                MSK and Sports Physio specializes in evidence-based physiotherapy for athletes and active individuals. 
                Our expert care combines manual therapy, exercise rehabilitation, and sports massage to help you 
                regain strength and restore movement.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Param has extensive experience working within both NHS and private healthcare settings, managing 
                conditions from acute injuries and post-operative recovery to rheumatological and complex 
                sports-related issues.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={() => window.location.href = '/contact'}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Book with Param
                </Button>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/param-jumani.jpg"
                  alt="Param Jumani - MSc qualified physiotherapist in Harrow"
                  width="800"
                  height="800"
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Facilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="overflow-hidden border-2 border-gray-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/images/facility-treatment-room.webp"
                  alt="Treatment Room - Physiotherapy clinic at HarroWall, Harrow"
                  width="800"
                  height="500"
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Treatment Room</h3>
                  <p className="text-white/90 text-sm">One to one physio sessions</p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 border-gray-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/images/facility-cardio.webp"
                  alt="Cardio Equipment Area at HarroWall physiotherapy clinic"
                  width="800"
                  height="500"
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Cardio Equipment</h3>
                  <p className="text-white/90 text-sm">Modern cardio machines including treadmills and exercise bikes</p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 border-gray-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/images/facility-gym.webp"
                  alt="Fully Equipped Gym - Physiotherapy rehab facility in Harrow"
                  width="800"
                  height="500"
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Fully Equipped Gym</h3>
                  <p className="text-white/90 text-sm">Complete free weights, dumbbells, and strength training equipment</p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 border-gray-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/images/facility-functional.webp"
                  alt="Functional Training Area at MSK and Sports Physio Harrow"
                  width="800"
                  height="500"
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Functional Training Area</h3>
                  <p className="text-white/90 text-sm">Cable machines, resistance bands, and functional training equipment</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 lg:px-8 bg-blue-600 text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Why Choose MSK and Sports Physio?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-blue-100">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insurance Partners */}
      <section className="py-16 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">We Work With Leading Insurance Providers</h3>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-12">
            {mockData.partners.map((partner, index) => (
              <div key={index} className="flex items-center justify-center transition-all duration-300 hover:scale-110">
                <img 
                  src={partner.logo} 
                  alt={`${partner.name} - approved insurance provider`}
                  width="120"
                  height="64"
                  loading="lazy"
                  className="h-16 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="py-20 px-4 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Conditions We Treat
            </h2>
            <p className="text-xl text-gray-600">
              Expert physiotherapy treatment for a wide range of musculoskeletal conditions in Harrow
            </p>
          </div>

          {/* Desktop Grid (tablet & up) */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conditions.map((cond, idx) => {
              const Icon = cond.icon;
              return (
                <Link
                  key={idx}
                  to={`/conditions/${cond.slug}`}
                  className="group block"
                >
                  <Card className="border-2 border-gray-200 group-hover:border-blue-500 group-hover:shadow-lg transition-all duration-300 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 group-hover:bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                          <Icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">{cond.title}</h3>
                          <p className="text-sm text-gray-600">{cond.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Mobile Carousel (below md) */}
          <div className="md:hidden">
            <MobileCarousel testId="conditions-carousel">
              {conditions.map((cond, idx) => {
                const Icon = cond.icon;
                return (
                  <Link key={idx} to={`/conditions/${cond.slug}`} className="block h-full">
                    <Card className="border-2 border-gray-200 h-full">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1 text-base">{cond.title}</h3>
                            <p className="text-sm text-gray-600">{cond.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </MobileCarousel>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Can't find your condition? We treat many other musculoskeletal and sports-related conditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/conditions'}
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
              >
                View All Conditions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => window.location.href = '/contact'}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Book Your Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 lg:px-8 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our Patients Say
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-900">5.0</span>
              <span className="text-gray-600">from verified Google reviews</span>
            </div>
          </div>

          {/* Desktop Grid (tablet & up) */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {mockData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100 hover:border-[#dbeafe] hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 text-sm leading-relaxed flex-grow">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t pt-4">
                    <div className="font-semibold text-base text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{testimonial.sport}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile Carousel (below md) */}
          <div className="md:hidden mb-10">
            <MobileCarousel testId="testimonials-carousel" interval={3500}>
              {mockData.testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-2 border-gray-100 h-full">
                  <CardContent className="p-5 flex flex-col h-full min-h-[420px]">
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 text-sm leading-relaxed flex-grow line-clamp-[12]">
                      "{testimonial.content}"
                    </p>
                    <div className="border-t pt-3 mt-auto">
                      <div className="font-semibold text-sm text-gray-900">{testimonial.name}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{testimonial.sport}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </MobileCarousel>
          </div>

          {/* See All Google Reviews Button */}
          <div className="text-center">
            <Button 
              onClick={() => window.open(mockData.googleMapsReviewUrl, '_blank')}
              size="lg"
              className="bg-white border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#2563eb] hover:text-white px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300"
            >
              <Star className="mr-2 w-5 h-5" />
              See All Google Reviews
            </Button>
            <p className="text-sm text-gray-500 mt-4">Read more verified reviews on Google Maps</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {mockData.faqs.map((faq) => (
              <Card key={faq.id} className="border-2 border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-4 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Start Your Recovery?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Book your initial assessment today and take the first step towards regaining strength and restoring movement
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Call Us</div>
                      <a href="tel:07747075809" className="font-semibold text-gray-900 hover:text-blue-600">
                        07747075809
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Email Us</div>
                      <a href="mailto:mskandsportsphysio@gmail.com" className="font-semibold text-gray-900 hover:text-blue-600 text-sm">
                        mskandsportsphysio@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Visit Us</div>
                      <div className="font-semibold text-gray-900 text-sm">
                        HarroWall Climbing Centre,<br/>
                        Neptune Trading Estate,<br/>
                        Neptune Rd, Harrow HA1 4HX
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-8 h-full flex flex-col justify-center items-center text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Have a Question?</h3>
                <p className="text-gray-600 mb-8 max-w-sm">
                  Send us your query through our secure contact form and we'll get back to you within 24 hours.
                </p>
                <Button
                  data-testid="submit-query-btn"
                  onClick={() => window.location.href = '/contact'}
                  className="w-full sm:w-auto bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-12 px-8 text-base font-semibold rounded-lg shadow-lg shadow-[#2563eb]/30 hover:shadow-xl hover:shadow-[#2563eb]/40 transition-all duration-200"
                >
                  Submit Your Query
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-xs text-gray-500 mt-6">
                  Or call us directly at{' '}
                  <a href="tel:07747075809" className="text-blue-600 font-semibold hover:underline">
                    07747075809
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
